import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import { getAuthenticatedUser } from '@/lib/get-authenticated-user'
import type { UserAttributes } from '@/shared/dtos'
import { CacheAdapter } from '@/shared/infra'

type CurrentUser = {
  attributes: UserAttributes
  authenticationFlowType: 'USER_SRP_AUTH'
  signInUserSession: {
    idToken: {
      jwtToken: string
      payload: Record<string, unknown>
    }
  }
}

interface AuthContextProps {
  user: CurrentUser | null
}

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps,
)

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<CurrentUser | null>(null)

  useEffect(() => {
    const userData = CacheAdapter.get('userData')

    if (Object.keys(userData).length > 0) {
      setUser(userData)
    } else {
      getAuthenticatedUser()
        .then(user => {
          CacheAdapter.set('userData', user)
          localStorage.removeItem('loginRequired')
          setUser(user)
          if (user?.attributes) {
            try {
              import('@sentry/react')
                .then(Sentry => {
                  Sentry.setUser({
                    id: user.attributes.sub as string,
                    email: user.attributes.email as string,
                    username: user.attributes.name as string,
                  })
                  Sentry.setTags({
                    userId: user.attributes.sub as string,
                    userEmail: user.attributes.email as string,
                    userLocale: (user.attributes.locale as string) || 'pt-BR',
                    loginTimestamp: new Date().toISOString(),
                  })
                  Sentry.setContext('auth_session', {
                    loginTime: new Date().toISOString(),
                    authFlow: user.authenticationFlowType,
                    emailVerified: user.attributes.email_verified,
                    phoneVerified: user.attributes.phone_number_verified,
                  })
                })
                .catch(importError => {
                  console.warn('Failed to import Sentry module:', importError)
                })
            } catch (sentryError) {
              console.warn('Sentry user context setup failed:', sentryError)
            }
          }
        })
        .catch(err => {
          console.log(err)
          try {
            import('@/shared/utils/sentry-custom-error')
              .then(({ captureCustomError }) => {
                captureCustomError({
                  message: 'Failed to get authenticated user on app startup',
                  error: err,
                  level: 'warning',
                  extraTags: {
                    authFlow: 'current_user_check',
                    errorType: 'auth_startup',
                    timestamp: new Date().toISOString(),
                  },
                  extra: {
                    errorCode: err.code,
                    errorName: err.name,
                    userAgent: navigator.userAgent,
                  },
                })
              })
              .catch(importError => {
                console.warn(
                  'Failed to import sentry-custom-error:',
                  importError,
                )
              })
          } catch (sentryError) {
            console.warn('Sentry auth error logging failed:', sentryError)
          }
        })
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user }}>{children}</AuthContext.Provider>
  )
}

export const useAuthContext = () => {
  const context = useContext(AuthContext)

  const [permissions, setPermissions] = useState({
    isCustomerGroupAdmin: false,
    isAgencyOfficeAdmin: false,
    isBookingIssuer: false,
    isAppUser: false,
    isSystemAdmin: false,
    isBatchBooker: false,
    isChainAgencyAdmin: false,
    isCustomerAdmin: false,
    isAgencyAdmin: false,
  })

  const { user } = context

  const userAuthorities: Record<string, string[]> =
    Object.keys(user ?? {}).length > 0
      ? JSON.parse(
          user?.signInUserSession.idToken.payload.authorities as string,
        )
      : {}

  const roles: string[] = userAuthorities.BOOKING_API ?? []

  useEffect(() => {
    const userAuthorities: Record<string, string[]> =
      Object.keys(user ?? {}).length > 0
        ? JSON.parse(
            user?.signInUserSession.idToken.payload.authorities as string,
          )
        : {}

    const hasAuthorities = Object.keys(userAuthorities).length > 0

    setPermissions({
      isAgencyAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('AGENCY_ADMIN')
        : false,
      isAgencyOfficeAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('AGENCY_OFFICE_ADMIN')
        : false,
      isAppUser: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('APP_USER')
        : false,
      isBatchBooker: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('BATCH_BOOKER')
        : false,
      isChainAgencyAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('CHAIN_AGENCY_ADMIN')
        : false,
      isCustomerAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('CUSTOMER_ADMIN')
        : false,
      isCustomerGroupAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('CUSTOMER_GROUP_ADMIN')
        : false,
      isSystemAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('SYS_ADMIN')
        : false,
      isBookingIssuer: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('BOOKING_ISSUER')
        : false,
    })
  }, [user])

  return { ...context, roles, ...permissions }
}
